import { ISiteInfoUrl } from "../src/taskpane/interfaces/ISiteInfo";
import { logger } from "../utilities/logger";
import dayjs from "dayjs";
import { IUrlKeyNamePair } from "../src/taskpane/interfaces/IUrlKeyNamePair";
import { IAdvancedSearchFields } from "../src/taskpane/interfaces/IAdvancedSearchFields"
import { IDocUrlKeyNamePair } from "../src/taskpane/interfaces/IDocUrlKeyNamePair";
import { shared } from "../src/taskpane/config/constants";
import { IDocUrlDropdown } from "../src/taskpane/interfaces/IDocUrlDropdown";
import LZString = require("lz-string");
import { StorageHelper } from "../utilities/StorageHelper";

interface ICacheLibrary extends IDocUrlDropdown {
    contentTypes: IDocUrlDropdown[];
    fields: IAdvancedSearchFields[];
}
interface IAdvancedSearchSite extends ISiteInfoUrl {
    libraries: ICacheLibrary[];
}

interface IAdvancedSearchCache {
    sites: IAdvancedSearchSite[];
    lastUpdate: Date;
}

export class AdvancedSearchCacheHandler {
    private static readonly cacheKeyBase: string = "@advancedSearchCache.";
    private storageKey: string = AdvancedSearchCacheHandler.cacheKeyBase;
    private now: dayjs.Dayjs;
    private cache: IAdvancedSearchCache;

    constructor(accountId: string) {
        this.now = dayjs.utc();
        try {
            if (accountId) {
                this.storageKey = AdvancedSearchCacheHandler.cacheKeyBase + accountId;
                this.getLocalStorage();
            } else {
                this.cache = null;
            }
        } catch (error) {
            logger(error, "advancedSearchUtils.ts (constructor)")
            this.cache = null;
        }
    }

    public setAccountId = (accountId: string) => {
        this.storageKey = AdvancedSearchCacheHandler.cacheKeyBase + accountId;
        this.getLocalStorage();
        // this.getUsedLocalStorageSpace();
    }

    private getLocalStorage = () => {
        const compressedCache = localStorage.getItem(this.storageKey);
        const decompressedCache = compressedCache ? LZString.decompressFromUTF16(compressedCache) : null;
        this.cache = JSON.parse(decompressedCache);
    }

    public setCache = (sites: IAdvancedSearchSite[]) => {
        try {
            const cache: IAdvancedSearchCache = {
                sites,
                lastUpdate: this.now.toDate(),
            };
            this.cache = cache;

            const stringCache = JSON.stringify(cache);
            const compressedCache = LZString.compressToUTF16(stringCache);
            localStorage.setItem(this.storageKey, compressedCache);
        } catch (error) {
            logger(error, "advancedSearchUtils.ts (setCache)")
        }
    }

    public getCache = (): IAdvancedSearchCache => {
        return this.cache;
    }

    public shouldUpdateCache = (): boolean => {
        try {
            if (!this.cache || this.now.diff(this.cache.lastUpdate, "day") > 7) {
                //if cache does not exist OR if cache has expired, refresh cache
                return true;
            }
            return false;
        } catch (error) {
            logger(error, "advancedSearchUtils.ts (shouldUpdateCache)");
            return false;
        }
    }

    public getSiteAndSubsites = (siteId: string, hubId: string, enableMultiSites: boolean): ISiteInfoUrl[] => {
        const sites: ISiteInfoUrl[] = this.getSites();

        if (!siteId || enableMultiSites) {
            return sites;
        }

        let mainSite: ISiteInfoUrl = sites.find(site => site.id === siteId);
        if (mainSite) {
            mainSite = { ...mainSite, isSubsite: false };
            const subsites = hubId
                ? sites.filter(site => site.id !== mainSite.id)
                : sites.filter(site => site.id !== mainSite.id && ((site.isChannel && site.teamSiteId === mainSite.id) || site.webUrl.includes(mainSite.webUrl + '/')));
            const mappedSubsites = subsites.map(s => {
                return ({
                    ...s,
                    isSubsite: true
                })
            });
            return [mainSite, ...mappedSubsites];
        }

        return [];

        // let mainSite: ISiteInfoUrl = sites.find(site => site.id === siteId);
        // if (mainSite) {
        //     mainSite = { ...mainSite, isSubsite: false };
        //     const subsites = sites.filter(site => {
        //         return (
        //             site.id !== mainSite.id
        //             && site.webUrl.includes(mainSite.webUrl + '/')
        //         )
        //     });
        //     const mappedSubsites = subsites.map(s => {
        //         return ({
        //             ...s,
        //             isSubsite: true
        //         })
        //     });
        //     return [mainSite, ...mappedSubsites];
        // }

        // return [];
    }

    public getSites = (): ISiteInfoUrl[] => {
        let sites: ISiteInfoUrl[] = [];
        if (this.cache) {
            this.cache.sites?.forEach(site => sites = sites.concat(site));
        }
        return sites;
    }

    public setSites = (newSites: ISiteInfoUrl[]) => {
        const sites = this.getCache()?.sites ?? [];
        const newSitesCache: IAdvancedSearchSite[] = newSites.map<IAdvancedSearchSite>(site => ({
            ...site,
            libraries: null,
        }));

        const newAndOldSites = newSitesCache.concat(sites);
        const isUnique = (new Set(newAndOldSites.map(s => s.id))).size === newAndOldSites.length;
        if (isUnique) {
            this.setCache(newAndOldSites);
            return;
        }

        // //if we have duplicates in the new and the old sites we need to remove the duplicates, keep the old info, but add the new ones
        // let updatedCacheSites: IAdvancedSearchSite[] = [];
        // newAndOldSites.forEach(site => {
        //     const ids = updatedCacheSites.map(s => s.id);
        //     if (!(ids.includes(site.id) && site.libraries === null)) {
        //         updatedCacheSites.push(site);
        //     }
        // });

        let unique = {};
        let distinct: IAdvancedSearchSite[] = [];
        for (let i = 0; i < newAndOldSites.length; i++) {
            if (!unique[newAndOldSites[i].id]) {
                distinct.push(newAndOldSites[i]);
                unique[newAndOldSites[i].id] = 1;
            }
        }

        this.setCache(distinct);
    }

    public getCachedLibraries = (selectedSites: IUrlKeyNamePair[]): IDocUrlDropdown[] => {
        let libraries: ICacheLibrary[] = [];
        const selectedSiteIds = selectedSites.map(site => site.siteId);
        this.cache.sites.forEach(site => {
            if (selectedSiteIds.includes(site.id)) {
                libraries = libraries.concat(site.libraries ?? []);
            }
        });
        let uniqueLibraries: ICacheLibrary[] = [];

        let libIds = libraries.map(lib => lib.driveId);
        const uniqueIds = new Set(libIds);
        uniqueIds.forEach(id => {
            uniqueLibraries.push(libraries.find(l => l.driveId === id));
        });

        if (uniqueLibraries.length != libraries.length) { //if we found duplicates, resave the library cache so that we don't have to do this unique-ify again
            // const uniqueDocUrlDropDown: IDocUrlDropdown[] = uniqueLibraries.map(lib => {
            //     delete lib.fields;
            //     return lib;
            // })
            this.setLibraries(selectedSites, uniqueLibraries);
        }

        return uniqueLibraries;
    }

    public sitesToUpdate = (currentSites: IUrlKeyNamePair[]): IUrlKeyNamePair[] => {
        if (this.cache) {
            const selectedIds: string[] = currentSites.map(s => s.siteId);
            const selectedCachedSites: IAdvancedSearchSite[] = this.cache.sites.filter(site => selectedIds.includes(site.id));
            const emptySites: IUrlKeyNamePair[] = selectedCachedSites.filter(site => !site.libraries).map(site => (
                { //see advanced search "setInitialSelectedSite" function for why this structure looks this way
                    webUrl: site.webUrl,
                    siteId: site.id,
                    key: site.key,
                    value: site.name,
                }
            ));
            return emptySites;
        } else {
            return currentSites;
        }
    }

    public setLibraries = (selectedSites: IUrlKeyNamePair[], libraries: IDocUrlDropdown[]) => {
        const selectedSiteIds = selectedSites.map(site => site.siteId);
        const newLibraries: ICacheLibrary[] = libraries.map(library => {
            return ({
                ...library,
                contentTypes: null,
                fields: null,
            })
        })

        const cachedSites: IAdvancedSearchSite[] = this.cache.sites.map<IAdvancedSearchSite>(site => {
            if (selectedSiteIds.includes(site.id)) {
                return {
                    ...site,
                    libraries: newLibraries.filter(lib => lib.siteId === site.id),
                }
            } else {
                return site;
            }

        })

        this.setCache(cachedSites);
        return libraries;
    }

    private getLibraries = (): ICacheLibrary[] => {
        let libs: ICacheLibrary[] = [];
        this.cache.sites
            .filter(s => s.libraries !== null)
            .forEach(s => {
                libs = libs.concat(s.libraries);
            })

        return libs;
    }

    public getEmptyContentLibs = (currentLibs: IDocUrlKeyNamePair[]): IDocUrlDropdown[] => {
        let emptyCachedLibs: ICacheLibrary[] = this.getLibraries().filter(l => !l.contentTypes);
        const currentLibsIds = currentLibs.map(l => l.sharePointListId);
        emptyCachedLibs = emptyCachedLibs.filter(l => currentLibsIds.includes(l.sharePointListId));

        return emptyCachedLibs;
    }

    public getCachedContentTypes = (currentLibs: IDocUrlKeyNamePair[]): IDocUrlDropdown[] => {
        let cachedContent: IDocUrlDropdown[] = [];
        this.getLibraries()
            .filter(l => l.contentTypes)
            .forEach(l => {
                cachedContent = cachedContent.concat(l.contentTypes);
            })
        const currentLibsIds = currentLibs.map(l => l.sharePointListId);
        cachedContent = cachedContent.filter(l => currentLibsIds.includes(l.sharePointListId));

        return cachedContent;
    }

    public setContentTypeItems = (contentTypeItems: IDocUrlDropdown[]) => {
        let updatedLibraries: ICacheLibrary[] = this.getLibraries()
            .map(l => {
                const updatedContentType = contentTypeItems.filter(c => c.sharePointListId === l.sharePointListId);
                return ({
                    ...l,
                    contentTypes: updatedContentType.length > 0 ? updatedContentType : l.contentTypes
                })
            });
        const updatedSites: IAdvancedSearchSite[] = this.cache.sites.map(site => {
            const libsToUpdate = updatedLibraries.filter(l => l.siteId === site.id);
            return ({
                ...site,
                libraries: libsToUpdate.length > 0 ? libsToUpdate : site.libraries,
            })
        })

        this.setCache(updatedSites);
    }

    public getEmptyFieldsLibs = (currentLibs: IDocUrlKeyNamePair[]): IDocUrlKeyNamePair[] => {
        let emptyCachedLibs: ICacheLibrary[] = this.getLibraries().filter(l => !l.fields);
        const emptyLibsIds: string[] = emptyCachedLibs.map(l => l.sharePointListId);
        const emptySelectedLibs: IDocUrlKeyNamePair[] = currentLibs.filter(l => emptyLibsIds.includes(l.sharePointListId));

        return emptySelectedLibs;
    }

    public getCachedFields = (currentLibs: IDocUrlKeyNamePair[]): IAdvancedSearchFields[][] => {
        let cachedLibs: ICacheLibrary[] = this.getLibraries().filter(l => l.fields);
        const currentLibsIds = currentLibs.map(l => l.sharePointListId);
        const fields = cachedLibs.map(l => l.fields).filter(f => f?.length > 0 ? currentLibsIds.includes(f[0].sharePointListId) : false);
        return fields;
    }

    public setFields = (fields: IAdvancedSearchFields[][]) => {
        let updatedLibraries: ICacheLibrary[] = this.getLibraries()
            .map(l => {
                const fieldRefIndex = fields.findIndex(f => f[0].sharePointListId === l.sharePointListId);
                if (Number.isSafeInteger(fieldRefIndex)) {
                    return ({
                        ...l,
                        fields: fields[fieldRefIndex],
                    })
                }
                return ({
                    ...l,
                    fields: l.fields,
                })
            });

        const updatedSites: IAdvancedSearchSite[] = this.cache.sites.map(site => {
            const libsToUpdate = updatedLibraries.filter(l => l.siteId === site.id);
            return ({
                ...site,
                libraries: libsToUpdate.length > 0 ? libsToUpdate : site.libraries,
            })
        })
        this.setCache(updatedSites);
    }

    public clearCache = () => {
        localStorage.removeItem(this.storageKey);
        this.cache = null;
    }

    private getUsedLocalStorageSpace() {
        const stringStorage = localStorage.getItem(this.storageKey);
        const tmp = stringStorage ? ((stringStorage.length * 16) / (8 * 1024)) : 0;
        var space = tmp.toFixed(2);
        console.log("space used:", space + " KB")
    };
}

// const params = new URLSearchParams(window.location.search);
// const CacheHandler: AdvancedSearchCacheHandler = params.has(shared.params.accountId)
//     ? new AdvancedSearchCacheHandler(params.get(shared.params.accountId))
//     : new AdvancedSearchCacheHandler("");
const CacheHandler: AdvancedSearchCacheHandler = new AdvancedSearchCacheHandler(StorageHelper.getItem(shared.params.accountId) ?? "");
export default CacheHandler;
